import { useAppSelector } from 'app/hooks';
import { differenceInMilliseconds } from 'date-fns';
import { selectSnapshotAlarming } from 'features/spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useEffect, useMemo, useState } from 'react';

export const useIsSnapshotAlarming = (snapshotId: number) => {
  const memoizedSnapshotAlarming = useMemo(selectSnapshotAlarming, [
    snapshotId,
  ]);
  const {
    alarming: snapshotAlarming,
    mostRecentIgnoreInterval,
    mostRecentTimestamp,
    startStopMode,
    warning,
  } = useAppSelector((state) => memoizedSnapshotAlarming(state, snapshotId));
  const [snapshotIsAlarming, setSnapshotIsAlarming] =
    useState<boolean>(snapshotAlarming);
  // Manage alarming state every time the memoized selector returns new values, i.e., we
  //  have a new alarm
  useEffect(() => {
    // Clear start/stop alarms that have stopped
    if (!snapshotAlarming && startStopMode) {
      setSnapshotIsAlarming(false);
      return;
    }
    // Skip alarm messages with bad or missing data
    if (
      !snapshotAlarming ||
      !mostRecentIgnoreInterval ||
      isNaN(Number(mostRecentIgnoreInterval))
    )
      return;
    // Set start/stop alarms to continue until we receive another alarm message
    if (startStopMode) {
      setSnapshotIsAlarming(true);
      return;
    }
    // Set snapshotIsAlarming to true if 1. the snapshot has an active alarm, and 2. the alarm
    //  hasn't expired via its ignore interval
    const ignoreIntervalMilliseconds = Number(mostRecentIgnoreInterval);
    const millisecondsSinceAlarmStarted = differenceInMilliseconds(
      new Date(),
      new Date(mostRecentTimestamp)
    );
    const timeRemainingOnAlarm =
      ignoreIntervalMilliseconds - millisecondsSinceAlarmStarted;
    if (snapshotAlarming && timeRemainingOnAlarm > 0)
      setSnapshotIsAlarming(true);
    // Turn off the alarm after it expires
    const timer = setTimeout(() => {
      setSnapshotIsAlarming(false);
    }, timeRemainingOnAlarm + 500);
    // Clear the timer when the component unmounts
    return () => clearTimeout(timer);
  }, [
    snapshotAlarming,
    mostRecentIgnoreInterval,
    mostRecentTimestamp,
    startStopMode,
  ]);
  return { snapshotIsAlarming, warning };
};
